import { Container, Typography, Box, Paper, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Paper elevation={3} sx={{ p: { xs: 3, md: 5 } }}>
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last Updated: March 18, 2026
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            ServeSync ("we," "our," or "us") is a volunteer coordination platform available at
            servesync.me (the "Service"). This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use the Service. By using the Service, you agree
            to the practices described in this policy.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            2. Information We Collect
          </Typography>
          <Typography variant="h6" gutterBottom>
            2.1 Information You Provide
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, and password when you register.</li>
              <li><strong>Profile Information:</strong> Phone number, skills, and organizational affiliations.</li>
              <li><strong>Event Data:</strong> Event registrations, volunteer assignments, and participation history.</li>
              <li><strong>Communications:</strong> Messages you send through our support form.</li>
            </ul>
          </Typography>
          <Typography variant="h6" gutterBottom>
            2.2 Information Collected Automatically
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            <ul>
              <li><strong>Usage Data:</strong> Pages visited, features used, and interactions with the Service.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers.</li>
              <li><strong>Log Data:</strong> IP address, access times, and referring URLs.</li>
            </ul>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            3. How We Use Your Information
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            We use the information we collect to:
            <ul>
              <li>Provide, maintain, and improve the Service.</li>
              <li>Create and manage your account.</li>
              <li>Match volunteers with events and organizations.</li>
              <li>Send email reminders about upcoming volunteer events you have registered for.</li>
              <li>Send SMS text message reminders if you have opted in (see Section 5).</li>
              <li>Respond to support requests and communicate with you about the Service.</li>
              <li>Generate analytics and reports for organizations about volunteer participation.</li>
              <li>Ensure the security and integrity of the Service.</li>
            </ul>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            4. Data Storage and Security
          </Typography>
          <Typography variant="body1" paragraph>
            Your data is stored using Google Firebase services, including Firebase Authentication and
            Cloud Firestore. Data is encrypted in transit using TLS and at rest on Google's servers.
            While we implement commercially reasonable security measures, no method of electronic
            storage is 100% secure, and we cannot guarantee absolute security.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            5. SMS Text Messaging
          </Typography>
          <Typography variant="body1" paragraph>
            ServeSync offers optional SMS text message reminders for upcoming volunteer events. This
            section describes how we handle SMS communications.
          </Typography>
          <Typography variant="h6" gutterBottom>
            5.1 Consent and Opt-In
          </Typography>
          <Typography variant="body1" paragraph>
            SMS messages are only sent to users who have explicitly opted in. To opt in, you must:
            (1) provide your phone number in your profile settings, and (2) enable the "SMS Reminders"
            toggle on your Profile page. SMS is disabled by default. When you enable SMS reminders,
            you will receive a confirmation text message.
          </Typography>
          <Typography variant="h6" gutterBottom>
            5.2 Types of Messages
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            If you opt in, you may receive:
            <ul>
              <li>A one-time confirmation message when you enable SMS reminders.</li>
              <li>Event reminder messages sent approximately 24 hours before events you are registered for, including the event name, time, location, and your assignment.</li>
            </ul>
          </Typography>
          <Typography variant="h6" gutterBottom>
            5.3 Message Frequency
          </Typography>
          <Typography variant="body1" paragraph>
            Message frequency varies based on your event registrations. You will receive at most one
            reminder per event you are registered for, sent the day before the event.
          </Typography>
          <Typography variant="h6" gutterBottom>
            5.4 Costs
          </Typography>
          <Typography variant="body1" paragraph>
            Message and data rates may apply. ServeSync does not charge for SMS messages, but your
            mobile carrier's standard messaging rates apply.
          </Typography>
          <Typography variant="h6" gutterBottom>
            5.5 Opt-Out
          </Typography>
          <Typography variant="body1" paragraph>
            You can stop receiving SMS messages at any time by: (1) replying STOP to any message
            from ServeSync, or (2) disabling the "SMS Reminders" toggle in your Profile settings.
            After opting out, you will receive a confirmation message and no further SMS messages
            will be sent.
          </Typography>
          <Typography variant="h6" gutterBottom>
            5.6 SMS Data Sharing
          </Typography>
          <Typography variant="body1" paragraph>
            Your phone number and SMS consent status are shared with our SMS service provider, Twilio,
            solely for the purpose of delivering text messages. We do not sell, rent, or share your
            phone number with third parties for marketing purposes. Your phone number and opt-in data
            will not be shared with any third parties for purposes unrelated to delivering the SMS
            messages you have consented to receive.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            6. Third-Party Services
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            We use the following third-party services to operate the platform:
            <ul>
              <li><strong>Google Firebase:</strong> Authentication, database storage, hosting, cloud functions, and usage analytics.</li>
              <li><strong>Google Analytics (GA4):</strong> Website analytics via Firebase Analytics. See Section 6.1 for details on cookies used.</li>
              <li><strong>SendGrid:</strong> Transactional email delivery (event confirmations, reminders, and announcements).</li>
              <li><strong>Twilio:</strong> SMS text message delivery for opted-in users.</li>
            </ul>
            These providers have their own privacy policies governing their handling of your data.
          </Typography>

          <Typography variant="h6" gutterBottom>
            6.1 Cookies and Analytics
          </Typography>
          <Typography variant="body1" paragraph>
            ServeSync uses Google Analytics (via Firebase Analytics) to understand how visitors use the
            site. Google Analytics sets the following cookies on your device:
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            <ul>
              <li><strong>_ga:</strong> Distinguishes unique users. Expires after 2 years.</li>
              <li><strong>_ga_9TM8THPV9Y:</strong> Stores session state. Expires after 2 years.</li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies collect anonymous information about pages visited, time spent on the site,
            and general usage patterns. They do not collect personally identifiable information.
            You can opt out of Google Analytics tracking by installing the{' '}
            <Link href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
              Google Analytics Opt-out Browser Add-on
            </Link>{' '}
            or by adjusting your browser's cookie settings.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            7. Data Sharing and Disclosure
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            We do not sell your personal information. We may share your information in the following
            circumstances:
            <ul>
              <li><strong>With Organizations:</strong> When you register for an event, the organizing entity can see your name, email, and assignment details for coordination purposes.</li>
              <li><strong>Service Providers:</strong> With third-party providers (listed in Section 6) as necessary to operate the Service.</li>
              <li><strong>Legal Requirements:</strong> If required by law, regulation, or legal process.</li>
              <li><strong>Safety:</strong> To protect the rights, safety, or property of ServeSync, our users, or the public.</li>
            </ul>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            8. Your Rights and Choices
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            You have the right to:
            <ul>
              <li><strong>Access:</strong> View the personal information we hold about you through your Profile page.</li>
              <li><strong>Update:</strong> Edit your profile information at any time.</li>
              <li><strong>Delete:</strong> Request deletion of your account and associated data by contacting us through our{' '}
                <Link component={RouterLink} to="/support">support page</Link>.</li>
              <li><strong>Opt-Out of SMS:</strong> Disable SMS reminders at any time (see Section 5.5).</li>
              <li><strong>Opt-Out of Email:</strong> Contact support to manage email notification preferences.</li>
              <li><strong>Opt-Out of Analytics:</strong> Use the Google Analytics Opt-out Browser Add-on or your browser's cookie settings to prevent analytics tracking.</li>
            </ul>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            9. Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            The Service is not intended for children under the age of 13. We do not knowingly collect
            personal information from children under 13. If we learn that we have collected information
            from a child under 13, we will delete it promptly.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            10. Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of material changes
            by posting the updated policy on this page and updating the "Last Updated" date. Your
            continued use of the Service after changes are posted constitutes acceptance of the revised
            policy.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            11. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy or our data practices, please contact us
            through our{' '}
            <Link component={RouterLink} to="/support">support page</Link>.
          </Typography>
          <Typography variant="body1">
            ServeSync - servesync.me
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicyPage;
